import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/site/Navbar';
import Footer from '../components/site/Footer';
import Reveal from '../components/site/Reveal';
import { POSTS as SEED_POSTS } from '../content/blog';
import { blogApi, resolveAsset } from '../lib/adminApi';
import { ArrowRight, Clock, Loader2 } from 'lucide-react';
import useSeo from '../lib/useSeo';

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
const readMinutes = (words) => Math.max(1, Math.round((words || 220) / 220));

// Convert an API post shape into the same view-model the seed posts use.
const fromApi = (p) => ({
  slug: p.slug,
  title: p.title,
  excerpt: p.excerpt,
  cover: resolveAsset(p.cover_url) || 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?auto=format&fit=crop&w=1600&q=80',
  category: p.category,
  date: p.publish_at || p.created_at,
  words: p.words,
});
const fromSeed = (p) => ({
  slug: p.slug, title: p.title, excerpt: p.excerpt, cover: p.cover,
  category: p.category, date: p.date, words: p.words,
});

const Blog = () => {
  useSeo({
    title: 'Insights — Audience revenue, lead-gen and B2B media strategy',
    description: 'Long-form perspective on turning publisher audiences into recurring revenue — content strategy, lead pricing, audience extension, and the operating model behind direct-sold lead-gen.',
    path: '/blog',
  });
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    blogApi.list(true)
      .then((rows) => {
        const api = (rows || []).map(fromApi);
        const seed = SEED_POSTS.map(fromSeed);
        const bySlug = new Map();
        [...seed, ...api].forEach((p) => bySlug.set(p.slug, p));
        const merged = [...bySlug.values()].sort((a, b) => new Date(b.date) - new Date(a.date));
        setPosts(merged);
      })
      .catch(() => setPosts(SEED_POSTS.map(fromSeed)))
      .finally(() => setLoading(false));
  }, []);

  const [featured, ...rest] = posts;

  return (
    <div className="min-h-screen bg-white font-sans text-navy">
      <Navbar />
      <main className="pt-28 pb-24">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <p className="text-brand-blue text-[12px] font-bold tracking-[0.16em] uppercase mb-4">Publisher Insights</p>
          <h1 className="text-navy font-black tracking-[-0.01em] leading-[1.05] text-[40px] sm:text-[52px] max-w-3xl">
            Field notes from the frontlines of B2B media revenue.
          </h1>
          <p className="mt-5 text-body-muted text-[17px] leading-[1.7] max-w-2xl">
            Practical, numbers-first thinking on how publishers are turning audience into durable revenue lines.
          </p>

          {loading && (
            <div className="mt-14 flex items-center gap-2 text-body-muted"><Loader2 className="h-4 w-4 animate-spin" /> Loading posts…</div>
          )}

          {!loading && featured && (
            <Reveal>
              <Link
                to={`/blog/${featured.slug}`}
                data-testid={`blog-featured-${featured.slug}`}
                className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-0 rounded-2xl bg-white border border-slate-100 shadow-mk overflow-hidden group hover:shadow-mk-lg transition-shadow duration-500"
              >
                <div className="lg:col-span-7 aspect-[16/9] overflow-hidden bg-slate-50">
                  <img src={featured.cover} alt="" loading="lazy" className="w-full h-full object-cover object-center group-hover:scale-[1.02] transition-transform duration-700" />
                </div>
                <div className="lg:col-span-5 p-8 md:p-10 flex flex-col justify-center">
                  <p className="text-brand-pink text-[11px] font-bold uppercase tracking-[0.18em]">Featured · {featured.category}</p>
                  <h2 className="mt-4 text-navy font-black tracking-[-0.005em] leading-[1.15] text-[28px] md:text-[32px]">{featured.title}</h2>
                  <p className="mt-4 text-body-muted text-[15.5px] leading-[1.7]">{featured.excerpt}</p>
                  <div className="mt-6 flex items-center gap-4 text-body-muted text-[13px]">
                    <span>{formatDate(featured.date)}</span>
                    <span className="text-slate-300">·</span>
                    <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {readMinutes(featured.words)} min read</span>
                  </div>
                  <div className="mt-6 inline-flex items-center gap-2 text-brand-blue text-[13px] font-bold uppercase tracking-[0.08em] group-hover:gap-3 transition-all duration-300">
                    Read the piece <ArrowRight className="h-4 w-4" />
                  </div>
                </div>
              </Link>
            </Reveal>
          )}

          {!loading && rest.length > 0 && (
            <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-8">
              {rest.map((p) => (
                <Reveal key={p.slug}>
                  <Link
                    to={`/blog/${p.slug}`}
                    data-testid={`blog-card-${p.slug}`}
                    className="block rounded-2xl bg-white border border-slate-100 shadow-mk overflow-hidden group hover:shadow-mk-lg hover:-translate-y-1 transition-all duration-500 h-full"
                  >
                    <div className="aspect-[16/9] overflow-hidden bg-slate-50">
                      <img src={p.cover} alt="" loading="lazy" className="w-full h-full object-cover object-center group-hover:scale-[1.03] transition-transform duration-700" />
                    </div>
                    <div className="p-6 md:p-7">
                      <p className="text-brand-blue text-[11px] font-bold uppercase tracking-[0.18em]">{p.category}</p>
                      <h3 className="mt-3 text-navy font-black tracking-[-0.005em] leading-[1.2] text-[22px]">{p.title}</h3>
                      <p className="mt-3 text-body-muted text-[14.5px] leading-[1.65]">{p.excerpt}</p>
                      <div className="mt-5 flex items-center gap-3 text-body-muted text-[12.5px]">
                        <span>{formatDate(p.date)}</span>
                        <span className="text-slate-300">·</span>
                        <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {readMinutes(p.words)} min read</span>
                      </div>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Blog;
